import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { CLINIC, LogoTile, doctorDisplay, doctorPhotoUrl, brandColor } from "../lib/clinic";
import { dietLabels } from "../lib/dietLabels";

/**
 * AdvancedDietPrintout — renders a schema_version=2 diet plan as a 3-page A4
 * printable document. Meant to be embedded inside `PrintDietPlan.jsx` when the
 * loaded plan's schema_version is 2.
 *
 * Language: uses plan.language by default. A "Language" pill picker in the
 * header (no-print) lets the reader flip English / Marathi / Hindi live —
 * section headings and prescription labels swap in place. The menu items
 * are printed in whatever language the AI generated (English or vernacular)
 * so no lossy translation happens client-side.
 */
export default function AdvancedDietPrintout({ plan, autoPrint = true }) {
  const [patient, setPatient] = useState(null);
  const [lang, setLang] = useState(plan?.language || "en");
  const T = dietLabels(lang);
  const printedOnce = useRef(false);

  useEffect(() => {
    if (!plan?.patient_id) return;
    (async () => {
      try {
        const r = await api.get(`/patients/${plan.patient_id}`);
        setPatient(r.data?.patient || null);
      } catch { /* ignore */ }
    })();
  }, [plan?.patient_id]);

  useEffect(() => {
    if (plan && autoPrint && !printedOnce.current) {
      printedOnce.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [plan, autoPrint]);

  if (!plan) return null;
  const px = plan.prescription || {};
  const anthro = plan.anthro || {};
  const cond = (plan.profile?.conditions || []).map((c) => c.code).join(" · ");
  const iso = plan.issued_at ? new Date(plan.issued_at).toLocaleDateString() : "";
  const patientHeader = patient
    ? `${patient.name} · ${patient.age || anthro.age || "?"} ${patient.sex || ""}`
    : (anthro.age ? `${anthro.age} ${anthro.sex || ""}` : "");

  const Cell = ({ label, value }) => (
    <div className="border border-slate-200 rounded px-2 py-1">
      <div className="text-[8px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-[11px] font-semibold text-slate-800">{value}</div>
    </div>
  );

  return (
    <div className="adv-diet-print bg-white text-slate-900 max-w-[210mm] mx-auto">
      <style>{`
        @page { size: A4; margin: 12mm; }
        .adv-diet-print { font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
        .adv-diet-print .page { page-break-after: always; padding: 8px 4px; }
        .adv-diet-print .page:last-child { page-break-after: auto; }
        .adv-diet-print h1, .adv-diet-print h2, .adv-diet-print h3 { color: #0f172a; }
        .adv-diet-print table { border-collapse: collapse; width: 100%; font-size: 10px; }
        .adv-diet-print th, .adv-diet-print td { border: 1px solid #e2e8f0; padding: 4px 6px; vertical-align: top; }
        .adv-diet-print th { background: #f8fafc; text-align: left; font-weight: 600; }
        .adv-diet-print .emergency { border: 2px solid #dc2626; background: #fef2f2; color: #991b1b; padding: 8px 12px; border-radius: 6px; }
        @media print { .adv-lang-picker { display: none !important; } }
      `}</style>

      {/* No-print language picker at the very top */}
      <div className="adv-lang-picker flex items-center justify-end gap-1 py-2 px-2 text-[11px]">
        <span className="text-slate-500 mr-1">Language:</span>
        {["en", "mr", "hi"].map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            className={`px-2 py-0.5 rounded border ${lang === code ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`adv-diet-print-lang-${code}`}
          >
            {code === "en" ? "EN" : code === "mr" ? "मराठी" : "हिंदी"}
          </button>
        ))}
      </div>

      {/* ── PAGE 1 ─────────────────────────────────────── */}
      <section className="page">
        <header className="flex items-start gap-4 border-b-2 pb-3 mb-4" style={{ borderColor: brandColor() }} data-testid="adv-diet-branded-header">
          <LogoTile className="h-14 w-14 shrink-0" padding="p-1.5" />
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight" style={{ color: brandColor() }}>{CLINIC.name}</h1>
            {CLINIC.specialties && <div className="text-[9.5px] font-semibold text-slate-600 uppercase tracking-wide">{CLINIC.specialties}</div>}
            {CLINIC.address && <div className="text-[11px] text-slate-600 leading-snug">{CLINIC.address}</div>}
            <div className="text-[10px] text-slate-500">{(CLINIC.phones || []).join(" · ")} {CLINIC.email ? `· ${CLINIC.email}` : ""} {CLINIC.timings ? `· ${CLINIC.timings}` : ""}</div>
          </div>
          <div className="flex items-start gap-2 shrink-0">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{T.title}</div>
              <div className="text-[10px] text-slate-500">{T.plan_id}: {plan.plan_id}</div>
              <div className="text-[10px] text-slate-500">{T.issued}: {iso}</div>
              <div className="text-[11px] font-semibold text-slate-800 mt-1">{doctorDisplay(CLINIC.doctorName)}</div>
            </div>
            <img
              src={doctorPhotoUrl()}
              alt={CLINIC.doctorName}
              className="h-12 w-12 rounded-full object-cover object-top border-2"
              style={{ borderColor: brandColor() }}
              data-testid="adv-diet-doctor-photo"
            />
          </div>
        </header>

        <div className="mb-4">
          <h2 className="text-sm font-bold mb-1">{T.patient_profile}</h2>
          <div className="text-[11px] text-slate-800"><strong>{patientHeader}</strong>{cond ? ` — ${cond}` : ""}</div>
          <div className="text-[10px] text-slate-600 mt-0.5">
            {T.height} {anthro.height_cm || "?"} cm · {T.weight} {anthro.weight_kg || "?"} kg
            {anthro.waist_cm ? ` · ${T.waist} ${anthro.waist_cm} cm` : ""}
            {anthro.reference_weight_kg ? ` · ${T.ref_weight} ${anthro.reference_weight_kg} kg (${anthro.reference_weight_basis})` : ""}
          </div>
        </div>

        <h2 className="text-sm font-bold mb-2">{T.prescription}</h2>
        <div className="grid grid-cols-4 gap-2 mb-4">
          <Cell label={T.energy} value={`${px.energy_kcal?.value ?? "?"} kcal`} />
          <Cell label={T.protein} value={`${px.protein?.grams ?? "?"} g @ ${px.protein?.g_per_kg ?? "?"} g/kg`} />
          <Cell label={T.carbs} value={`${px.carbs_g?.value ?? "?"} g`} />
          <Cell label={T.fat} value={`${px.fat_g?.value ?? "?"} g`} />
          <Cell label={T.sodium} value={`${px.sodium_mg ?? "?"} mg`} />
          <Cell label={T.salt_total} value={`≤${px.salt_g ?? "?"} g/day`} />
          <Cell label={T.fluid} value={`${px.fluid_ml?.value ?? "?"} ml/day`} />
          <Cell label={T.fibre} value={`${px.fibre_g?.value ?? "?"} g`} />
          <Cell label={T.cooking_oil} value={`${px.cooking_oil_tsp ?? "?"} tsp/day`} />
          <Cell label={T.meals} value={`${px.meals_count?.main ?? 3} main + ${px.meals_count?.snacks ?? 2} snack`} />
          <Cell label={T.loss_pace} value={`${px.weight_loss_pace_kg_per_week ?? 0} kg/wk`} />
          <Cell label={T.guidelines} value={(plan.generated_by?.guidelines_cited || []).slice(0, 2).join(" · ")} />
        </div>

        {plan.clinical_rationale_md && (
          <div className="text-[10px] text-slate-700 whitespace-pre-wrap leading-relaxed border border-slate-200 rounded p-2">
            {plan.clinical_rationale_md}
          </div>
        )}

        {plan.portion_glossary?.length > 0 && (
          <div className="mt-3">
            <h3 className="text-[11px] font-bold mb-1">{T.portion_glossary}</h3>
            <ul className="text-[9px] text-slate-700 columns-2 gap-4 pl-3 list-disc">
              {plan.portion_glossary.map((p, i) => (
                <li key={i}><strong>{p.term}:</strong> {p.definition_en || p.definition}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── PAGE 2: 7-day menu ─────────────────────────── */}
      <section className="page">
        <h2 className="text-sm font-bold mb-2">{T.meal_plan}</h2>
        {(plan.menu_7day || []).length === 0 ? (
          <div className="text-[10px] text-slate-500 italic">{T.menu_unavailable}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: "6%" }}>{T.day}</th>
                <th style={{ width: "23%" }}>{T.breakfast}</th>
                <th style={{ width: "23%" }}>{T.lunch}</th>
                <th style={{ width: "12%" }}>{T.evening}</th>
                <th style={{ width: "24%" }}>{T.dinner}</th>
                <th style={{ width: "12%" }}>{T.snacks}</th>
              </tr>
            </thead>
            <tbody>
              {plan.menu_7day.map((d, i) => {
                const meals = d.meals || [];
                const by = (name) => meals.find((m) => (m.meal || "").toLowerCase().includes(name));
                const listOf = (m) => (m?.items || []).map((it) => `${it.name_en || it.name || ""}${it.qty_desc || it.qty_desc_en ? " (" + (it.qty_desc || it.qty_desc_en) + ")" : ""}`).join(" · ");
                const bf = by("break") || meals[0];
                const lu = by("lunch") || by("noon");
                const ev = by("evening") || by("mid");
                const di = by("dinner");
                const snacks = meals.filter((m) => !/(break|lunch|dinner|evening|snack|mid)/.test((m.meal || "").toLowerCase())).map((m) => listOf(m)).join("; ");
                return (
                  <tr key={i}>
                    <td><strong>{T.day} {d.day || i + 1}</strong></td>
                    <td>{listOf(bf)}</td>
                    <td>{listOf(lu)}</td>
                    <td>{listOf(ev)}</td>
                    <td>{listOf(di)}</td>
                    <td>{snacks || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* ── PAGE 3: Foods-to-avoid + monitoring + emergency ─── */}
      <section className="page space-y-3">
        {plan.foods_to_avoid?.length > 0 && (
          <div>
            <h2 className="text-sm font-bold mb-2">{T.foods_to_avoid}</h2>
            <table>
              <thead>
                <tr><th style={{ width: "22%" }}>{T.category}</th><th>{T.items}</th></tr>
              </thead>
              <tbody>
                {plan.foods_to_avoid.map((cat, i) => (
                  <tr key={i}>
                    <td><strong>{cat.reason}</strong></td>
                    <td>{(cat.items || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {plan.electrolytes && (plan.electrolytes.potassium?.if_elevated || plan.electrolytes.phosphorus?.if_elevated) && (
          <div>
            <h3 className="text-[11px] font-bold mb-1">{T.electrolyte_guide}</h3>
            {plan.electrolytes.potassium?.if_elevated && (
              <div className="text-[10px] mb-1">
                <strong>If K↑:</strong> Reduce {(plan.electrolytes.potassium.if_elevated.reduce || []).join(", ")}. Prefer {(plan.electrolytes.potassium.if_elevated.prefer || []).join(", ")}. Prep: {plan.electrolytes.potassium.if_elevated.prep}
              </div>
            )}
            {plan.electrolytes.phosphorus?.if_elevated && (
              <div className="text-[10px]">
                <strong>If PO₄↑:</strong> Avoid additives {(plan.electrolytes.phosphorus.if_elevated.avoid_additives || []).join(", ")}. Individualise {(plan.electrolytes.phosphorus.if_elevated.individualise || []).join(", ")}
              </div>
            )}
          </div>
        )}

        {plan.medication_interactions?.length > 0 && (
          <div>
            <h3 className="text-[11px] font-bold mb-1">{T.drug_diet}</h3>
            <ul className="text-[10px] list-disc pl-4 space-y-0.5">
              {plan.medication_interactions.map((m, i) => {
                const drug = typeof m === "string" ? "" : m.drug;
                const rule = typeof m === "string" ? m : m.rule;
                return <li key={i}>{drug && <strong>{drug}: </strong>}{rule}</li>;
              })}
            </ul>
          </div>
        )}

        {plan.monitoring?.length > 0 && (
          <div>
            <h3 className="text-[11px] font-bold mb-1">{T.monitoring}</h3>
            <table>
              <thead><tr><th>{T.parameter}</th><th style={{ width: "35%" }}>{T.frequency}</th></tr></thead>
              <tbody>
                {plan.monitoring.map((m, i) => (
                  <tr key={i}><td>{m.parameter}</td><td>{m.frequency}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {plan.emergency_signs?.length > 0 && (
          <div className="emergency">
            <div className="text-[11px] font-bold mb-1">{T.emergency}</div>
            <ul className="text-[10px] list-disc pl-4 space-y-0.5">
              {plan.emergency_signs.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}

        <div className="text-[9px] text-slate-500 pt-3 border-t border-slate-200">
          {T.issued_by} {plan.issued_by || doctorDisplay(CLINIC.doctorName)} · {T.guidelines}: {(plan.generated_by?.guidelines_cited || []).join(" · ")}. {T.disclaimer}
        </div>

        <footer
          className="mt-3 rounded px-4 py-3 text-white"
          style={{ backgroundColor: brandColor(), WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
          data-testid="adv-diet-branded-footer"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[12px] font-extrabold">{CLINIC.name}</div>
              {CLINIC.address && <div className="text-[9.5px] opacity-90 leading-snug">{CLINIC.address}</div>}
              <div className="text-[9.5px] opacity-90 flex flex-wrap items-center gap-x-3 mt-0.5">
                {(CLINIC.phones || []).length > 0 && <span>📞 {CLINIC.phones.join(" · ")}</span>}
                {CLINIC.website && <span>🌐 {CLINIC.website}</span>}
                {CLINIC.email && <span>✉ {CLINIC.email}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] font-semibold">{doctorDisplay(CLINIC.doctorName)}</div>
              {CLINIC.timings && <div className="text-[9px] opacity-90">{CLINIC.timings}</div>}
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
